import { IAccountRespSchema, IRefundSchema } from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MBox,
  MFlex,
  MHStack,
  MPageHeader,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { getRefundView } from '../../../constants/routes';
import { useAccountRefunds } from '../../../hooks/AccountDetail/useAccountRefunds';
import { PaymentMethodModal } from '../PaymentMethods/PaymentMethodModal';
import {
  ACCOUNT_REFUND_LIST_CATALOG,
  AccountRefundListColumnContext,
} from './accountRefundList.columns';
import { RefundActions } from './RefundActions';

interface AccountRefundListProps {
  account: IAccountRespSchema;
}

export const AccountRefundList = ({ account }: AccountRefundListProps) => {
  const navigate = useNavigate();
  const { id: accountId } = account;
  const {
    filters,
    setFilters,
    onResetFilter,
    totalRecords,
    setPager,
    pager,
    loading,
    listData,
    refetch,
  } = useAccountRefunds({ accountId });

  const tableRef = useRef<DataTableRef>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  const onSelectRow = (rowData: IRefundSchema) => {
    navigate(getRefundView({ accountId, refundId: rowData.id }));
  };

  const actionBodyTemplate = useCallback(
    (refund: IRefundSchema) => <RefundActions refundId={refund.id} />,
    [],
  );

  const onPaymentMethodClick = useCallback(
    (paymentMethodId: string) => {
      setSelectedPaymentMethodId(paymentMethodId);
    },
    [setSelectedPaymentMethodId],
  );

  const columnContext = useMemo<AccountRefundListColumnContext>(
    () => ({ actionBodyTemplate, onPaymentMethodClick }),
    [actionBodyTemplate, onPaymentMethodClick],
  );
  const { columns, state: columnState } = useTableColumns(
    ACCOUNT_REFUND_LIST_CATALOG,
    columnContext,
  );

  const hasFilerApplied = filters && filters?.length > 0;
  const hasContent = listData?.content?.length > 0;
  const shouldShowHeaderContent = hasContent || hasFilerApplied;

  const handleNewTableFiltersChange = (newFilters: FilterType[]) => {
    setFilters(newFilters);
    setPager({ ...pager, page: 0, first: 0 });
  };

  return (
    <MBox>
      <MPageHeader title="Refunds" hideContent={!shouldShowHeaderContent}>
        <MFlex>
          <MHStack gap="2">
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="accountRefunds"
              filters={filters}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `accountDocuments-${accountId}.csv`}
            />
          </MHStack>
        </MFlex>
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={totalRecords}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        onRowClick={(row) => onSelectRow(row)}
        onFiltersChange={handleNewTableFiltersChange}
        initialFilters={filters}
        resetFilter={() => {
          onResetFilter();
        }}
        emptyProps={{
          mainMessage: 'Looks like there are no refunds here.',
        }}
      />
      {selectedPaymentMethodId && (
        <PaymentMethodModal
          isOpen={!!selectedPaymentMethodId}
          onClose={() => setSelectedPaymentMethodId(null)}
          paymentMethodId={selectedPaymentMethodId}
        />
      )}
    </MBox>
  );
};
